import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import { logger } from '@/lib/logger'
import { ApiError } from '@/api/api-error'

/**
 * Retry policy: never retry client errors (4xx) — they will not succeed.
 * Retry network/5xx failures twice with exponential backoff.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false
  if (error instanceof ApiError) {
    if (error.isNetworkError) return true
    return error.status >= 500
  }
  return false
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: shouldRetry,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: false,
      throwOnError: false,
    },
    mutations: {
      retry: false,
    },
  },
  // Toasts are raised by the Axios interceptor; here we only log so a single
  // failure never produces two notifications.
  queryCache: new QueryCache({
    onError: (error, query) => {
      logger.error('[query] failed', { key: query.queryKey, error })
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      logger.error('[mutation] failed', { key: mutation.options.mutationKey, error })
    },
  }),
})
